import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import useSWR from 'swr';
import { Layout, LoadingSpinner, ErrorMessage } from '@/shared/components';
import { useFormSubmission } from '@/shared/hooks/useFormSubmission';
import { useNotificationActions } from '@/shared/stores/notification-store';
import { DoctorForm } from '@/features/doctors/DoctorForm';
import { DoctorImageManager } from '@/features/doctors/DoctorImageManager';
import { doctorsApi } from '@/shared/api';
import type { DoctorFormData } from '@/shared/schemas';

/**
 * Страница редактирования врача
 * Содержит форму редактирования и управление изображениями
 */
export const DoctorEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showSuccess } = useNotificationActions();

  // id из useParams может отсутствовать: тогда parseInt даёт NaN, и запрос
  // ниже не уходит — так было и с утверждением, только молча.
  const doctorId = parseInt(id ?? '', 10);

  // Загрузка данных врача
  const {
    data: doctorResponse,
    error: loadError,
    isLoading: isLoadingDoctor,
    mutate
  } = useSWR(doctorId ? ['doctor', doctorId] : null, () =>
    doctorsApi.getById(doctorId)
  );

  const doctor = doctorResponse?.doctor;

  /**
   * Хук для управления отправкой формы с уведомлениями
   */
  const { isSubmitting, handleSubmit } = useFormSubmission<DoctorFormData>({
    onSubmit: async (data: DoctorFormData) => {
      // Prepare data for API, handling optional properties properly
      const apiData = {
        fullName: data.fullName,
        specialization: data.specialization,
        description: data.description,
        educationPrimary: data.educationPrimary,
        educationAdvanced: data.educationAdvanced,
        interestsArea: data.interestsArea,
        principles: data.principles,
        experience: data.experience,
        isActive: data.isActive,
        isAvailableOnline: data.isAvailableOnline,
        isAvailableAtHome: data.isAvailableAtHome,
        isAvailableInTheOffice: data.isAvailableInTheOffice,
        sortOrder: data.sortOrder,
        proDoctorovReviewsWidgetCode: data.proDoctorovReviewsWidgetCode || null,
        ...(data.slug && { slug: data.slug }),
        onSiteBookingUrl: data.onSiteBookingUrl || null,
      };
      await doctorsApi.update(doctorId, apiData);
      await mutate(); // Обновляем данные
    },
    successMessage: 'Информация о враче успешно обновлена!',
    errorMessage: 'Ошибка обновления врача'
  });

  /**
   * Обработчик успешной загрузки изображения
   */
  const handleImageUploadSuccess = (): void => {
    mutate(); // Обновляем данные врача
    showSuccess('Изображение успешно загружено!');
  };

  /**
   * Обработчик успешного удаления изображения
   */
  const handleImageDeleteSuccess = (): void => {
    mutate(); // Обновляем данные врача
    showSuccess('Изображение успешно удалено!');
  };

  /**
   * Обработчик успешной установки активного изображения
   */
  const handleSetActiveImageSuccess = (): void => {
    mutate(); // Обновляем данные врача
    showSuccess('Активное изображение успешно установлено!');
  };

  // Показываем загрузку
  if (isLoadingDoctor) {
    return (
      <Layout title="Редактирование врача">
        <div className="flex justify-center py-8">
          <LoadingSpinner size="lg" text="Загрузка данных врача..." />
        </div>
      </Layout>
    );
  }

  // Показываем ошибку загрузки
  if (loadError || !doctor) {
    return (
      <Layout title="Редактирование врача">
        <div className="space-y-6">
          <ErrorMessage message="Ошибка загрузки данных врача. Проверьте правильность ID или попробуйте позже." />
          <Link to="/doctors" className="btn btn-primary">
            <i className="fas fa-arrow-left mr-2"></i>
            Вернуться к списку врачей
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title={`Редактирование: ${doctor.fullName}`}>
      <div className="space-y-6">
        {/* Навигация */}
        <div className="flex items-center gap-2 text-sm">
          <Link to="/doctors" className="text-primary hover:text-primary/80">
            Врачи
          </Link>
          <i className="fas fa-chevron-right text-base-content/40"></i>
          <span className="text-base-content/60">Редактирование</span>
        </div>

        {/* Заголовок */}
        <div className="card card-bordered bg-base-100 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-primary to-primary/80 rounded-xl flex items-center justify-center">
              <i className="fas fa-user-edit text-white"></i>
            </div>
            <div>
              <h2 className="text-xl font-bold text-base-content">
                Редактирование врача
              </h2>
              <p className="text-base-content/60 mt-1">
                Обновите информацию о враче и управляйте изображениями
              </p>
            </div>
          </div>
        </div>

        {/* Управление изображениями */}
        <DoctorImageManager
          doctor={doctor}
          onUploadSuccess={handleImageUploadSuccess}
          onDeleteSuccess={handleImageDeleteSuccess}
          onSetActiveSuccess={handleSetActiveImageSuccess}
        />

        {/* Форма редактирования врача */}
        <DoctorForm
          doctor={doctor}
          onSubmit={handleSubmit}
          isLoading={isSubmitting}
          submitText="Сохранить изменения"
        />

        {/* Дополнительные действия */}
        <div className="card card-bordered bg-base-200/50 shadow-sm">
          <div className="card-body p-2">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-base-content">
                  Дополнительные действия
                </h3>
                <p className="text-sm text-base-content/60 mt-1">
                  Управление статусом врача и другие операции
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => navigate('/doctors')}
                  className="btn btn-ghost"
                >
                  <i className="fas fa-arrow-left"></i>К списку врачей
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};
